import { useEffect, useState } from 'react';

import { apiStrapi } from 'services/apiService';

// Cache simples para evitar múltiplas requisições
let cachedData: {
  mediaUrl: string | null;
  mediaType: string | null;
  error: Error | null;
  isLoaded: boolean;
} | null = null;

// Promise global para deduplicar chamadas
let loadingPromise: Promise<void> | null = null;

export const useDynamicLogoForm = () => {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (cachedData?.isLoaded) {
      setMediaUrl(cachedData.mediaUrl);
      setMediaType(cachedData.mediaType);
      setError(cachedData.error);
      setIsLoading(false);
      return;
    }

    // Se já existe uma promise em andamento, aguarda ela
    if (loadingPromise) {
      loadingPromise.then(() => {
        if (isMounted && cachedData) {
          setMediaUrl(cachedData.mediaUrl);
          setMediaType(cachedData.mediaType);
          setError(cachedData.error);
          setIsLoading(false);
        }
      });
      return;
    }

    // Cria a promise e salva globalmente
    loadingPromise = (async () => {
      try {
        const response = await apiStrapi.get('/organizations/onboarding-logo');
        const data = response.data;
        if (data && data.url && data.mime) {
          let url = data.url;
          let mime = data.mime;
          if (data.formats) {
            let largestFormat = Object.entries(data.formats).reduce(
              (largest, [key, format]) => {
                const currentFormat = format as { sizeInBytes: number; url: string; mime: string };
                if (!largest || currentFormat.sizeInBytes > largest.sizeInBytes) {
                  return { key, ...currentFormat };
                }
                return largest;
              },
              null as { key: string; sizeInBytes: number; url: string; mime: string } | null
            );
            if (largestFormat) {
              url = largestFormat.url;
              mime = largestFormat.mime;
            }
          }
          cachedData = {
            mediaUrl: url,
            mediaType: mime,
            error: null,
            isLoaded: true,
          };
          if (isMounted) {
            setMediaUrl(url);
            setMediaType(mime);
            setError(null);
            setIsLoading(false);
          }
        } else {
          throw new Error('Invalid logo form data format');
        }
      } catch (apiError) {
        cachedData = {
          mediaUrl: null,
          mediaType: null,
          error: apiError as Error,
          isLoaded: true,
        };
        if (isMounted) {
          setMediaUrl(null);
          setMediaType(null);
          setError(apiError as Error);
          setIsLoading(false);
        }
      } finally {
        loadingPromise = null; // Libera a promise para futuras chamadas
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  return { mediaUrl, mediaType, isLoading, error };
};
